import { Box } from "@chakra-ui/react-v3";
import { Bundle } from "@medplum/fhirtypes";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useQuery } from "@tanstack/react-query";
import FlexSearch from "flexsearch";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getCareGaps } from "../../../api/care-gaps";
import { getSuspects } from "../../../api/suspects";
import { useSearchFilter } from "../../../hooks/useSearchFilter";
import { MedicalKeywordSearch, parseSearchQuery } from "../../search";
import { useSharedContext } from "../shared-logic/shared-context";
import { BrowseContent, SelectedRowState, BrowseTableData } from "./browse-content";
import { CollateDocumentsButton } from "./components/collate-documents-button";
import { ContentLoadingSkeleton } from "./components/content-loading-skeleton";
import { CollateDocumentsModal } from "./components/export-modal";
import { PdfViewerModal } from "./components/pdf-viewer-modal/index";
import { SidePanelDrawer } from "./components/side-panel-drawer";
import { PatientTabsV2 } from "./components/tabs";
import { Z_INDEX } from "./constants/z-index";
import { useDocumentExport } from "./hooks/use-document-export";
import { useResourceAnalysisCache } from "./hooks/use-resource-analysis-cache";
import { useRowViewState } from "./hooks/use-row-view-state";
import { useSidePanel } from "./hooks/use-side-panel";
import { SearchResultsContent, FilteredTableData } from "./search-results-content";
import { SidePanelContent } from "./side-panels/sidepanel-content";
import { getCareGapRows } from "./tables/care-gaps/care-gaps-rows";
import { getSuspectRows } from "./tables/suspect/suspect-rows";
import { filterOneMedicalResources } from "./utils/one-medical-filter";
import { BROWSE_TABLE_METADATA, SEARCH_TABLE_METADATA, TableRow } from "./utils/tables-metadata";

type PatientOverviewV2ContentProps = {
  fhirSdk: FhirBundleSdk | undefined;
  children: React.ReactNode;
  isImpersonating: boolean;
  isLoadingData?: boolean;
  isOneMedicalFilterEnabled?: boolean;
  consolidated?: Bundle;
  patientId: string;
  hasAiSummary?: boolean;
  isEhr?: boolean;
  headerHeight?: number;
  /** From `CxFeatureFlags.suspectsSectionEnabled`; when false, section is not mounted (no API request). */
  isSuspectsSectionEnabled?: boolean;
  /** From `CxFeatureFlags.careGapsSectionEnabled`; when false, section is not mounted (no API request). */
  isCareGapsSectionEnabled?: boolean;
};

export function PatientOverviewV2Content({
  fhirSdk,
  children,
  isImpersonating,
  isLoadingData = false,
  isOneMedicalFilterEnabled = false,
  consolidated,
  patientId,
  hasAiSummary = false,
  isEhr = false,
  headerHeight = 0,
  isSuspectsSectionEnabled = false,
  isCareGapsSectionEnabled = false,
}: PatientOverviewV2ContentProps) {
  const {
    sharedState: { isUserInSandbox, featureFlags },
  } = useSharedContext();
  const [isIndexing, setIsIndexing] = useState(false);
  const [hasFeaturedData, setHasFeaturedData] = useState(false);

  const { filterQuery, filterInputRef, handleFilterChange, handleClear, setFilter } =
    useSearchFilter();
  // Lift side panel state to prevent child rerenders
  const { open, openSidePanel, handleOpenChange } = useSidePanel();
  const [selectedRowState, setSelectedRowState] = useState<SelectedRowState | undefined>();

  // Determine if user is searching
  const isSearching = !!filterQuery.trim();

  const showSuspects = isSuspectsSectionEnabled || !!isUserInSandbox;
  const showCareGaps = isCareGapsSectionEnabled || !!isUserInSandbox;

  // Apply One Medical filtering if enabled
  const sdkToUse = useMemo(() => {
    if (!fhirSdk) return undefined;
    return isOneMedicalFilterEnabled && consolidated
      ? FhirBundleSdk.createSync(filterOneMedicalResources(consolidated))
      : fhirSdk;
  }, [fhirSdk, isOneMedicalFilterEnabled, consolidated]);

  const { data: suspectBundleResp } = useQuery({
    queryKey: ["suspects", patientId, isUserInSandbox],
    queryFn: () => getSuspects(patientId, !!isUserInSandbox),
    enabled: showSuspects,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const { data: careGapBundleResp } = useQuery({
    queryKey: ["careGaps", patientId, isUserInSandbox],
    queryFn: () => getCareGaps(patientId, !!isUserInSandbox),
    enabled: showCareGaps,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const suspectRows = useMemo(
    () => getSuspectRows({ bundles: suspectBundleResp?.results }),
    [suspectBundleResp]
  );

  const careGapRows = useMemo(
    () => getCareGapRows({ bundles: careGapBundleResp?.results }),
    [careGapBundleResp]
  );

  // Build indexed table data for SEARCH mode at page load
  // This is the expensive operation that builds FlexSearch indexes
  const searchIndexedTableData = useMemo(() => {
    if (!sdkToUse) return [];

    const standard = SEARCH_TABLE_METADATA.map(table => {
      const rows = table.getRows(sdkToUse);

      // Create and populate the FlexSearch index
      const index = new FlexSearch.Index({
        tokenize: "forward",
      });

      const indexingPromises: Promise<FlexSearch.Index>[] = [];
      rows.forEach((row, idx) => {
        const searchableText = table.getSearchableText
          ? table.getSearchableText(row)
          : buildDefaultSearchableText(row);

        indexingPromises.push(index.addAsync(idx, searchableText));
      });

      return {
        tableType: table.tableType,
        displayName: table.displayName,
        rows,
        index,
        indexingPromises,
        isEmptySection: rows.length === 0,
      };
    });

    const analytics = [];

    if (showSuspects) {
      const suspectIndex = new FlexSearch.Index({ tokenize: "forward" });
      const suspectIndexingPromises: Promise<FlexSearch.Index>[] = [];
      suspectRows.forEach((row, idx) => {
        suspectIndexingPromises.push(suspectIndex.addAsync(idx, buildDefaultSearchableText(row)));
      });
      analytics.push({
        tableType: "Suspects",
        displayName: "Suspects",
        rows: suspectRows,
        index: suspectIndex,
        indexingPromises: suspectIndexingPromises,
        isEmptySection: suspectRows.length === 0,
      });
    }

    if (showCareGaps) {
      const careGapIndex = new FlexSearch.Index({ tokenize: "forward" });
      const careGapIndexingPromises: Promise<FlexSearch.Index>[] = [];
      careGapRows.forEach((row, idx) => {
        careGapIndexingPromises.push(careGapIndex.addAsync(idx, buildDefaultSearchableText(row)));
      });
      analytics.push({
        tableType: "CareGaps",
        displayName: "Care Gaps",
        rows: careGapRows,
        index: careGapIndex,
        indexingPromises: careGapIndexingPromises,
        isEmptySection: careGapRows.length === 0,
      });
    }

    return [...standard, ...analytics];
  }, [sdkToUse, suspectRows, careGapRows, showSuspects, showCareGaps]);

  // Build table data for BROWSE mode (no indexing needed)
  const browseTableData: BrowseTableData[] = useMemo(() => {
    if (!sdkToUse) return [];

    const standard = BROWSE_TABLE_METADATA.map(table => {
      const rows = table.getRows(sdkToUse);
      return {
        tableType: table.tableType,
        displayName: table.displayName,
        rows,
        isEmptySection: rows.length === 0,
      };
    });
    const analytics: BrowseTableData[] = [];
    if (showSuspects) {
      analytics.push({
        tableType: "Suspects",
        displayName: "Suspects",
        rows: suspectRows,
        isEmptySection: suspectRows.length === 0,
      });
    }
    if (showCareGaps) {
      analytics.push({
        tableType: "CareGaps",
        displayName: "Care Gaps",
        rows: careGapRows,
        isEmptySection: careGapRows.length === 0,
      });
    }
    return [...standard, ...analytics];
  }, [sdkToUse, suspectRows, careGapRows, showSuspects, showCareGaps]);

  // Track indexing state
  useEffect(() => {
    if (searchIndexedTableData.length === 0) return;

    setIsIndexing(true);

    const allPromises = searchIndexedTableData.flatMap(data => data.indexingPromises);
    Promise.all(allPromises)
      .then(() => {
        setIsIndexing(false);
      })
      .catch(error => {
        console.error("Error during indexing:", error);
        setIsIndexing(false);
      });
  }, [searchIndexedTableData]);

  // Filter search data based on query (cheap operation, runs on every keystroke)
  const searchFilteredTableData: FilteredTableData[] = useMemo(() => {
    return searchIndexedTableData.map(tableData => {
      const filteredRows = filterRows(tableData.rows, tableData.index, filterQuery);

      return {
        ...tableData,
        filteredRows,
        isEmptySection: filteredRows.length === 0,
      };
    });
  }, [searchIndexedTableData, filterQuery]);

  // Build table data for tabs based on current mode.
  const tabsTableData: FilteredTableData[] = useMemo(() => {
    if (isSearching) {
      return searchFilteredTableData;
    }
    return browseTableData.map(table => ({
      ...table,
      filteredRows: table.rows,
      index: {} as never,
      indexingPromises: [],
    }));
  }, [isSearching, searchFilteredTableData, browseTableData]);

  // Lift PDF modal state to parent
  const [pdfModalState, setPdfModalState] = useState<{
    isOpen: boolean;
    docId: string;
    docFilename: string;
    initialSearchQuery?: string;
  }>({
    isOpen: false,
    docId: "",
    docFilename: "",
    initialSearchQuery: undefined,
  });

  // Initialize analysis cache in parent
  const { cache, buildCacheKey, startAnalysis } = useResourceAnalysisCache({
    bundle: fhirSdk,
    isEnabled: featureFlags?.shouldShowSidepanelAiAnalysis,
  });

  // Initialize row view state for tracking viewed/unviewed rows
  const { isRowViewed, markRowAsViewed } = useRowViewState(patientId);

  const onRowSelect = useCallback(
    (rowState: SelectedRowState) => {
      setSelectedRowState(rowState);
      openSidePanel();

      // Mark row as viewed immediately on click
      markRowAsViewed(rowState.tableType, rowState.row.id);

      // Trigger analysis for the selected row
      const rowData = rowState.row as Record<string, unknown>;
      const data = {
        type: "single" as const,
        resource: rowData.originalData,
        metadata: {
          date: (rowData.date ?? rowData.datePerformed ?? rowData.firstSeen ?? "-") as string,
          displayName: (rowData.name ??
            rowData.displayName ??
            rowData.allergy ??
            rowData.condition ??
            rowData.description ??
            "Unknown") as string,
        },
      };

      startAnalysis({
        data,
        rowData: rowState.row,
        tableType: rowState.tableType,
        rowId: rowState.row.id,
        suspectsContext: rowState.suspectsContext,
      });
    },
    [openSidePanel, startAnalysis, markRowAsViewed]
  );

  // Handler for when a document is downloaded from raw documents table
  const handleDocumentDownloaded = useCallback(
    (docId: string) => {
      markRowAsViewed("DocumentReference", docId);
    },
    [markRowAsViewed]
  );

  // Get analysis state for selected row
  const analysisState = useMemo(() => {
    if (!selectedRowState) {
      return undefined;
    }

    const uuid = buildCacheKey(selectedRowState.tableType, selectedRowState.row.id);
    return cache[uuid];
  }, [selectedRowState, cache, buildCacheKey]);

  // Initialize document export hook for side panel
  const {
    exportList,
    onAddToExport,
    isDocumentInExportList,
    isExporting,
    removeDocument,
    clearAll,
    exportCollatedPdf,
  } = useDocumentExport(patientId);

  const [isCollateDocsModalOpen, setIsCollateDocsModalOpen] = useState(false);

  // PDF modal handlers
  const handlePdfView = useCallback(
    (docId: string, docFilename?: string, initialSearchQuery?: string) => {
      setPdfModalState({
        isOpen: true,
        docId,
        docFilename: docFilename ?? docId,
        initialSearchQuery,
      });
    },
    []
  );

  const handleClosePdfModal = useCallback(() => {
    setPdfModalState({
      isOpen: false,
      docId: "",
      docFilename: "",
      initialSearchQuery: undefined,
    });
  }, []);

  const isImpersonatingOffset = isImpersonating ? 45 : 0;
  const stickyTopOffset = isEhr ? headerHeight : isImpersonatingOffset;

  return (
    <>
      <Box
        id="sticky-header"
        py={2}
        position={"sticky"}
        top={`${stickyTopOffset}px`}
        zIndex={Z_INDEX.STICKY_HEADER}
        backgroundColor="bg.canvas"
      >
        <MedicalKeywordSearch
          filterInputRef={filterInputRef}
          filterQuery={filterQuery}
          handleFilterChange={handleFilterChange}
          handleClear={handleClear}
          setFilter={setFilter}
          isLoadingData={isLoadingData}
          isIndexing={isIndexing}
        />
        <PatientTabsV2
          hasAiSummary={hasAiSummary}
          isLoadingData={isLoadingData}
          hasFeaturedData={hasFeaturedData}
          filteredTableData={tabsTableData}
        />
      </Box>

      {/* Mode switch: Loading / Browse / Search */}
      {isLoadingData || !sdkToUse ? (
        <ContentLoadingSkeleton />
      ) : isSearching ? (
        <SearchResultsContent
          fhirSdk={sdkToUse}
          filterQuery={filterQuery}
          filteredTableData={searchFilteredTableData}
          onRowSelect={onRowSelect}
          onPdfView={handlePdfView}
          isRowViewed={isRowViewed}
          onDocumentDownloaded={handleDocumentDownloaded}
        >
          {children}
        </SearchResultsContent>
      ) : (
        <BrowseContent
          fhirSdk={sdkToUse}
          browseTableData={browseTableData}
          onFeaturedDataChange={setHasFeaturedData}
          onRowSelect={onRowSelect}
          onPdfView={handlePdfView}
          isRowViewed={isRowViewed}
          onDocumentDownloaded={handleDocumentDownloaded}
        >
          {children}
        </BrowseContent>
      )}

      {/* Render side panel in parent to prevent child rerenders when it opens/closes */}
      {selectedRowState && fhirSdk && (
        <SidePanelDrawer open={open} onOpenChange={handleOpenChange}>
          <SidePanelContent
            selectedRowState={selectedRowState}
            analysisState={analysisState}
            fhirSdk={fhirSdk}
            onAddToExport={onAddToExport}
            onRemoveFromExport={removeDocument}
            isDocumentInExportList={isDocumentInExportList}
            onPdfView={handlePdfView}
          />
        </SidePanelDrawer>
      )}

      {/* PDF Viewer Modal - Portal renders at top level with z-index above side panel */}
      <PdfViewerModal
        isOpen={pdfModalState.isOpen}
        onClose={handleClosePdfModal}
        docId={pdfModalState.docId}
        docFilename={pdfModalState.docFilename}
        initialSearchQuery={pdfModalState.initialSearchQuery}
        onAddToExport={onAddToExport}
        onRemoveFromExport={removeDocument}
        isDocumentInExportList={isDocumentInExportList}
        exportList={exportList}
      />
      <CollateDocumentsButton
        count={exportList.length}
        onClick={() => setIsCollateDocsModalOpen(true)}
      />
      <CollateDocumentsModal
        isOpen={isCollateDocsModalOpen}
        onClose={() => setIsCollateDocsModalOpen(false)}
        exportList={exportList}
        isExporting={isExporting}
        onRemoveDocument={removeDocument}
        clearAll={clearAll}
        onExport={exportCollatedPdf}
      />
    </>
  );
}

/**
 * Builds default searchable text from a row by concatenating all non-metadata fields
 */
function buildDefaultSearchableText(row: Record<string, unknown>): string {
  const rowValues = Object.entries(row)
    .filter(([key]) => key !== "originalData" && key !== "id")
    .map(([, value]) => String(value ?? ""))
    .join(" ");

  return rowValues.toLowerCase();
}

/**
 * Filters out rows based on the user facing filterbar using a pre-built FlexSearch index.
 * Supports OR-based multi-term search: searching "vitamin hello" returns rows containing
 * either "vitamin" OR "hello" OR both.
 * Supports quoted phrases: searching '"t score"' keeps the phrase together.
 */
function filterRows(rows: TableRow[], index: FlexSearch.Index, filterQuery: string): TableRow[] {
  if (!filterQuery.trim()) {
    return rows;
  }

  // Parse query respecting quoted phrases and convert to lowercase
  const terms = parseSearchQuery(filterQuery, { lowercase: true });

  if (terms.length === 0) {
    return rows;
  }

  // Search each term separately and combine results (OR logic)
  const matchingIndicesSet = new Set<number>();

  terms.forEach(term => {
    const termMatches = index.search(term) as number[];
    termMatches.forEach(idx => matchingIndicesSet.add(idx));
  });

  // Return rows that match at least one term
  return rows.filter((_, idx: number) => matchingIndicesSet.has(idx));
}
